import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { GraduationCap, Users, Handshake, Download, Laptop, Smartphone, Apple, Clock, ArrowLeft, WifiOff, RefreshCw } from "lucide-react";
import { formatBytes, detectOS } from "../components/AppReleaseCard";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-portal-252/artifacts/ymujr84m_Logo%20blitzz%20%2B%20BYB%20ok%20sans%20fond%20copie.png";

const APPS = [
  { key: "formation", name: "Blitzz Formation", icon: GraduationCap, color: "#f7c935",
    audience: "Élèves, formateurs et centres de formation partenaires",
    desc: "Sessions, modules SCORM, émargement, projection et espace stagiaire. Chaque profil accède à son espace selon ses droits : l'élève à ses formations, le formateur à ses sessions, le centre partenaire à ses stagiaires et documents.",
    roles: ["Élève : formations, quiz, attestations", "Formateur : sessions, émargement, notation", "Centre partenaire : inscriptions, suivi, documents"] },
  { key: "salarie", name: "Blitzz Salarié", icon: Users, color: "#38bdf8",
    audience: "Salariés des sociétés du groupe",
    desc: "Application dédiée aux salariés : notifications, messagerie, mon espace (bulletins, congés, tâches), pointage QR / borne et mode hors ligne avec synchronisation automatique.",
    roles: ["Notifications & messagerie", "Mon espace : bulletins, documents, tâches", "Pointage hors ligne, badge, rappels"] },
  { key: "partenaire", name: "Blitzz Partenaire", icon: Handshake, color: "#4ade80",
    audience: "Partenaires, fournisseurs et apporteurs d'affaires",
    desc: "Les partenaires déposent leurs factures, suivent leurs commissions et échangent avec l'équipe ; les sociétés valident leurs commandes depuis le même espace.",
    roles: ["Dépôt de factures fournisseurs", "Validation des commandes", "Commissions & messagerie"] },
];

const PLATFORMS = [
  ["windows", "Windows", Laptop, "Windows 10 / 11 — 64 bits"],
  ["android", "Android", Smartphone, "Android 8+ (APK)"],
  ["mac", "macOS", Apple, "macOS 12+"],
];

function DownloadButton({ platform, label, Icon, hint, release, recommended, color }) {
  if (!release) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/5 text-slate-600 text-xs" data-testid={`dl-${platform}-soon`}>
        <Clock className="w-3.5 h-3.5" /> {label} — bientôt disponible
      </div>
    );
  }
  const href = release.download_url.startsWith("http") ? release.download_url : `${process.env.REACT_APP_BACKEND_URL}${release.download_url}`;
  return (
    <a href={href} className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${recommended ? "border-transparent" : "border-white/10 hover:bg-white/5"}`}
      style={recommended ? { background: color, color: "#14101c" } : {}} data-testid={`dl-${platform}`}>
      <Icon className="w-4 h-4" />
      <span className="flex-1 text-left">
        <span className="text-sm font-bold block">{label} <span className="font-normal opacity-70">v{release.version}</span></span>
        <span className="text-[10px] opacity-70 block">{hint} · {formatBytes(release.size)}</span>
      </span>
      <Download className="w-4 h-4" />
    </a>
  );
}

export default function DownloadsPage() {
  const [data, setData] = useState(null);
  const [societe, setSociete] = useState(null);
  const [error, setError] = useState("");
  const os = detectOS();
  const { slug } = useParams();

  const load = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/app-releases/all`, { params: slug ? { societe: slug } : {} }).then(r => { setData(r.data.apps || {}); setSociete(r.data.societe || null); setError(""); }).catch(() => setError("Impossible de charger les versions"));
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#0b0811] text-white" data-testid="downloads-page">
      <header className="max-w-6xl mx-auto px-6 pt-8 flex items-center justify-between">
        <Link to="/login" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="downloads-back"><ArrowLeft className="w-4 h-4" /> Connexion</Link>
        <img src={LOGO_URL} alt="Blitzz" className="h-12 object-contain" />
      </header>
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="max-w-2xl mb-12">
          {societe && (
            <div className="flex items-center gap-3 mb-4" data-testid="downloads-societe">
              {societe.logo_url && <img src={societe.logo_url} alt={societe.name} className="h-10 object-contain" />}
              <span className="text-sm font-bold" style={{ color: societe.primary || "#f7c935" }}>{societe.name}</span>
            </div>
          )}
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#f7c935] font-bold mb-3">Applications</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-none mb-4">{societe ? <>Les applications <span style={{ color: societe.primary || "#f7c935" }}>{societe.name}</span></> : <>Trois applications, <span className="text-[#f7c935]">un seul compte.</span></>}</h1>
          <p className="text-slate-400 text-base">Installez l'application qui correspond à votre profil. Toutes fonctionnent hors ligne, se mettent à jour automatiquement et utilisent vos identifiants habituels. Vos droits déterminent ce que vous voyez.</p>
          <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1"><WifiOff className="w-3.5 h-3.5" /> Mode hors ligne</span>
            <span className="flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Mises à jour automatiques</span>
            {error && <button onClick={load} className="text-amber-400 underline" data-testid="downloads-retry">{error} — réessayer</button>}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {APPS.map((app, i) => {
            const platforms = data?.[app.key]?.platforms || {};
            const Icon = app.icon;
            return (
              <section key={app.key} className="bg-[#14101c] border border-white/5 rounded-2xl p-6 flex flex-col animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: `${i * 90}ms`, animationFillMode: "both" }} data-testid={`app-card-${app.key}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${app.color}1a`, color: app.color }}><Icon className="w-6 h-6" /></div>
                <h2 className="text-lg font-black">{app.name}</h2>
                <div className="text-xs font-bold mt-1" style={{ color: app.color }}>{app.audience}</div>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">{app.desc}</p>
                <ul className="mt-4 space-y-1.5 text-xs text-slate-300">
                  {app.roles.map(r => <li key={r} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: app.color }} /> {r}</li>)}
                </ul>
                <div className="mt-6 space-y-2">
                  {PLATFORMS.map(([p, label, PIcon, hint]) => (
                    <DownloadButton key={p} platform={p} label={label} Icon={PIcon} hint={hint} release={platforms[p]} recommended={os === p && !!platforms[p]} color={app.color} />
                  ))}
                  {!data && !error && <div className="text-xs text-slate-600">Chargement des versions…</div>}
                </div>
              </section>
            );
          })}
        </div>

        <p className="mt-10 text-xs text-slate-600 max-w-3xl">Sur tablette ou téléphone sans installateur, ouvrez la plateforme dans votre navigateur puis « Ajouter à l'écran d'accueil » : la version web installable (PWA) offre le même mode hors ligne. Les installateurs Windows ne sont pas signés : au premier lancement, cliquez sur « Informations complémentaires » puis « Exécuter quand même ».</p>
      </main>
    </div>
  );
}
